import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';
import { hiltiMaterialsAPI, hiltiProductsAPI } from '../services/api';
import { HiltiMaterial, HiltiProduct } from '../types';
import { showCustomAlert } from './CustomAlert';

interface MaterialRow {
  materialId: string;
  quantityPerUnit: number;
}

const HiltiProducts: React.FC = () => {
  const [products, setProducts] = useState<HiltiProduct[]>([]);
  const [allMaterials, setAllMaterials] = useState<Record<string, HiltiMaterial[]>>({});
  const [loading, setLoading] = useState(false);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<HiltiProduct | null>(null);
  const [name, setName] = useState('');
  const [materials, setMaterials] = useState<MaterialRow[]>([]);
  const [error, setError] = useState('');

  const dialogHeader = useMemo(
    () => (editing ? 'Izmeni Hilti proizvod' : 'Dodaj Hilti proizvod'),
    [editing]
  );

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await hiltiProductsAPI.getAll();
      setProducts(data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju Hilti proizvoda';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProducts();
  }, []);

  const loadMaterialsForProduct = async (productId: string) => {
    if (allMaterials[productId]) return;
    try {
      const mats = await hiltiMaterialsAPI.getAllForProduct(productId);
      setAllMaterials(prev => ({ ...prev, [productId]: mats }));
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Greška pri učitavanju materijala za izabrani proizvod';
      showCustomAlert.error(msg);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setName('');
    setMaterials([]);
    setError('');
    setDialogVisible(true);
  };

  const openEdit = async (product: HiltiProduct) => {
    setEditing(product);
    setName(product.name);
    setMaterials(
      (product.materials || []).map(m => ({
        materialId: m.materialId,
        quantityPerUnit: m.quantityPerUnit
      }))
    );
    setError('');
    await loadMaterialsForProduct(product._id);
    setDialogVisible(true);
  };

  const addMaterialRow = () => {
    setMaterials(prev => [...prev, { materialId: '', quantityPerUnit: 0 }]);
  };

  const updateMaterialRow = (index: number, patch: Partial<MaterialRow>) => {
    setMaterials(prev => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeMaterialRow = (index: number) => {
    setMaterials(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Naziv proizvoda je obavezan');
      return;
    }

    const sanitizedMaterials = materials
      .filter(m => m.materialId && m.quantityPerUnit > 0)
      .map(m => ({
        materialId: m.materialId,
        quantityPerUnit: m.quantityPerUnit
      }));

    try {
      setError('');
      if (editing?._id) {
        await hiltiProductsAPI.update(editing._id, {
          name: name.trim(),
          materials: sanitizedMaterials
        });
        showCustomAlert.success('Proizvod je ažuriran');
      } else {
        await hiltiProductsAPI.create({
          name: name.trim(),
          materials: sanitizedMaterials
        });
        showCustomAlert.success('Proizvod je dodat');
      }
      setDialogVisible(false);
      await loadProducts();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju Hilti proizvoda';
      setError(msg);
      showCustomAlert.error(msg);
    }
  };

  const deleteProduct = async (product: HiltiProduct) => {
    if (!window.confirm(`Da li ste sigurni da želite da obrišete proizvod "${product.name}"?`)) {
      return;
    }
    try {
      await hiltiProductsAPI.delete(product._id);
      await loadProducts();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju proizvoda';
      showCustomAlert.error(msg);
    }
  };

  const materialsBodyTemplate = (product: HiltiProduct) => {
    if (!product.materials || product.materials.length === 0) {
      return <span className="text-600">Nema definisanih materijala</span>;
    }
    return (
      <div className="flex flex-wrap gap-2">
        {product.materials.map(mat => (
          <span
            key={mat.materialId}
            className="px-2 py-1 border-round text-sm"
            style={{ background: '#e0f2fe', color: '#0369a1' }}
          >
            {mat.materialName} ({mat.quantityPerUnit} {mat.unit})
          </span>
        ))}
      </div>
    );
  };

  const materialOptionsForCurrent = useMemo(() => {
    if (!editing) return [];
    const mats = allMaterials[editing._id] || [];
    return mats.map(m => ({ label: `${m.name} (${m.unit})`, value: m._id }));
  }, [editing, allMaterials]);

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Hilti Proizvodi" subTitle="Lista proizvoda i materijala po proizvodu">
        {error && !dialogVisible && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="flex justify-content-between align-items-center mb-3">
          <Button label="Dodaj proizvod" icon="pi pi-plus" onClick={openCreate} />
          <Button label="Osveži" icon="pi pi-refresh" outlined onClick={loadProducts} />
        </div>

        <DataTable
          value={products}
          loading={loading}
          paginator
          rows={15}
          rowsPerPageOptions={[15, 30, 50]}
          responsiveLayout="stack" breakpoint="768px"
        >
          <Column field="name" header="Naziv" sortable />
          <Column header="Materijali" body={materialsBodyTemplate} />
          <Column
            header="Akcije"
            style={{ width: '180px' }}
            body={(row: HiltiProduct) => (
              <div className="flex gap-2 justify-content-end">
                <Button
                  icon="pi pi-pencil"
                  className="action-icon-btn"
                  text
                  rounded
                  onClick={() => openEdit(row)}
                  tooltip="Izmeni"
                />
                <Button
                  icon="pi pi-trash"
                  className="action-icon-btn"
                  text
                  rounded
                  severity="danger"
                  onClick={() => deleteProduct(row)}
                  tooltip="Obriši"
                />
              </div>
            )}
          />
        </DataTable>
      </Card>

      <Dialog
        header={dialogHeader}
        visible={dialogVisible}
        style={{ width: '720px', maxWidth: '96vw' }}
        onHide={() => setDialogVisible(false)}
        modal
        closable
      >
        <div className="p-fluid">
          {error && (
            <div style={{ marginBottom: '1rem' }}>
              <Message severity="error" text={error} />
            </div>
          )}

          <div className="field">
            <label htmlFor="productName" style={{ fontWeight: 600 }}>
              Naziv proizvoda*
            </label>
            <InputText
              id="productName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Unesite naziv Hilti proizvoda"
            />
          </div>

          {editing && (
            <div className="field" style={{ marginTop: '1.5rem' }}>
              <div className="flex justify-content-between align-items-center mb-3">
                <label style={{ fontWeight: 600 }}>Materijali po proizvodu</label>
                <Button
                  icon="pi pi-plus"
                  label="Dodaj materijal"
                  text
                  onClick={async () => {
                    await loadMaterialsForProduct(editing._id);
                    addMaterialRow();
                  }}
                />
              </div>

              <div
                className="border-1 surface-border border-round"
                style={{ overflow: 'hidden' }}
              >
                <div className="flex px-3 py-2 border-bottom-1 surface-border text-sm font-semibold bg-gray-50">
                  <div className="flex-1">Materijal</div>
                  <div style={{ width: '190px' }}>Količina po proizvodu</div>
                  <div style={{ width: '70px', textAlign: 'right' }}>Akcije</div>
                </div>

                {materials.length === 0 && (
                  <div className="px-3 py-3 text-600 text-sm">
                    Nema dodatih materijala. Kliknite na „Dodaj materijal“ da dodate prvi red.
                  </div>
                )}

                {materials.map((row, idx) => (
                  <div
                    key={idx}
                    className="flex align-items-center px-3 py-2 border-bottom-1 surface-border gap-2"
                  >
                    <div className="flex-1">
                      <Dropdown
                        value={row.materialId}
                        options={materialOptionsForCurrent}
                        onChange={(e) =>
                          updateMaterialRow(idx, { materialId: e.value as string })
                        }
                        placeholder="Izaberite materijal"
                        className="w-full"
                        appendTo="self"
                      />
                    </div>
                    <div style={{ width: '190px' }}>
                      <InputNumber
                        value={row.quantityPerUnit}
                        onValueChange={(e) =>
                          updateMaterialRow(idx, { quantityPerUnit: e.value || 0 })
                        }
                        mode="decimal"
                        minFractionDigits={0}
                        maxFractionDigits={3}
                        className="w-full"
                      />
                    </div>
                    <div style={{ width: '70px', textAlign: 'right' }}>
                      <Button
                        icon="pi pi-trash"
                        text
                        rounded
                        severity="danger"
                        onClick={() => removeMaterialRow(idx)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-content-end" style={{ marginTop: '1.5rem' }}>
            <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} />
            <Button
              label="Otkaži"
              icon="pi pi-times"
              severity="secondary"
              outlined
              onClick={() => setDialogVisible(false)}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default HiltiProducts;


